import React from 'react';
import getBackendUrl from './utils/backendConfig';
import { exportRubriqueToPDF, copyToClipboard, shareContent, exportToText } from './utils/exportUtils';

const RubriquePage = ({ 
  onGoBack, 
  rubriqueNumber, 
  rubriqueTitle, 
  content, 
  bookInfo,
  onNavigateToRubrique,
  // Nouvelles props pour les boutons fonctionnels
  selectedBook = "Genèse",
  selectedChapter = "1",
  selectedVerse = "--",
  selectedLength = 500,
  setCurrentPage,
  API_BASE,
  setContent,
  setRubriquesStatus,
  setIsLoading,
  isLoading = false,
  BASE_RUBRIQUES = []
}) => {
  
  // Debug: Afficher le contenu reçu
  console.log(`[RUBRIQUE PAGE DEBUG] Rubrique ${rubriqueNumber}:`);
  console.log(`[RUBRIQUE PAGE DEBUG] Content type:`, typeof content);
  console.log(`[RUBRIQUE PAGE DEBUG] Content length:`, content ? content.length : 0);
  console.log(`[RUBRIQUE PAGE DEBUG] Content preview:`, content ? content.slice(0, 100) + "..." : "VIDE");
  console.log(`[RUBRIQUE PAGE DEBUG] Content truthy:`, !!content);

  // État pour le modal API
  const [isApiModalOpen, setIsApiModalOpen] = React.useState(false);
  const [apiStatus, setApiStatus] = React.useState(null);
  const [apiHistory, setApiHistory] = React.useState(null);

  // Fonctions pour gérer le modal API
  const fetchApiStatus = async () => {
    if (!API_BASE) return;
    try {
      const response = await fetch(`${API_BASE}/api-status`);
      if (response.ok) {
        const status = await response.json();
        setApiStatus(status);
        console.log('[RUBRIQUE API] Statut récupéré:', status);
      }
    } catch (error) {
      console.error('[RUBRIQUE API] Erreur statut:', error);
    }
  };

  const fetchApiHistory = async () => {
    if (!API_BASE) return;
    try {
      const response = await fetch(`${API_BASE}/api-history`);
      if (response.ok) {
        const history = await response.json();
        setApiHistory(history);
        console.log('[RUBRIQUE API] Historique récupéré:', history);
      }
    } catch (error) {
      console.error('[RUBRIQUE API] Erreur historique:', error);
    }
  };

  // Charger les données API au montage du composant
  React.useEffect(() => {
    if (isApiModalOpen) {
      fetchApiStatus();
      fetchApiHistory();
    }
  }, [isApiModalOpen, API_BASE]);

  // Fonctions utilitaires pour l'affichage API
  const getLedColor = (api) => {
    return api.color === 'green' ? '#00ff00' : '#ff0000';
  };

  const ledStyle = (color) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: color,
    marginRight: '12px',
    boxShadow: `0 0 8px ${color}`,
    animation: 'pulse-led 2s infinite ease-in-out'
  });

  const getStatusIcon = (api) => {
    return api.color === 'green' ? '✅' : '❌';
  };

  // Fonctions de gestion des boutons - COPIÉES DE LA PAGE PRINCIPALE

  // 1. Fonction Gemini (copiée de App.js)
  const handleGeminiAction = async () => {
    if (!API_BASE || !setContent || !setRubriquesStatus || !setIsLoading) {
      console.warn("[RUBRIQUE] API functions not available");
      return;
    }

    try {
      setIsLoading(true); 
      setContent("🎓 Enrichissement théologique approfondi en cours...\n\nGénération d'un contenu digne d'une école de théologie évangélique avec explications claires des termes complexes...");
      setRubriquesStatus(p => ({ ...p, [rubriqueNumber]: "in-progress" }));

      const passage = (selectedVerse === "--" || selectedVerse === "vide")
        ? `${selectedBook} ${selectedChapter}`
        : `${selectedBook} ${selectedChapter}:${selectedVerse}`;

      // Enrichir théologiquement avec approche académique
      if (rubriqueNumber >= 1 && rubriqueNumber <= 28) {
        const rubriqueTitle = BASE_RUBRIQUES[rubriqueNumber] || `Rubrique ${rubriqueNumber}`;
        
        console.log(`[ENRICHISSEMENT THÉOLOGIQUE] Rubrique ${rubriqueNumber} - ${rubriqueTitle}`);
        
        // Appeler le nouvel endpoint d'enrichissement théologique
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 secondes
        
        const response = await fetch(`${getBackendUrl()}/api/enrich-theological`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            passage: passage,
            book: selectedBook,
            chapter: selectedChapter,
            rubrique_number: rubriqueNumber,
            rubrique_title: rubriqueTitle,
            current_content: "" // Pas de contenu existant pour RubriquePage
          }),
          signal: controller.signal
        }).finally(() => clearTimeout(timeoutId));

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }

        const data = await response.json();
        console.log("[ENRICHISSEMENT THÉOLOGIQUE] Contenu reçu:", data.content ? data.content.length : 0, "caractères");

        if (data.content) {
          // Afficher l'enrichissement avec titre de rubrique
          const finalContent = `# Étude Théologique - ${passage}\n\n## ${rubriqueNumber}. ${rubriqueTitle}\n\n${data.content}`;
          
          setContent(finalContent);
          setRubriquesStatus(p => ({ ...p, [rubriqueNumber]: "completed" }));
          
          console.log("[SUCCESS] Enrichissement théologique affiché correctement");
        } else {
          throw new Error("Pas de contenu reçu de l'enrichissement théologique");
        }
      }
    } catch (err) {
      console.error("Erreur enrichissement théologique:", err);
      setContent(`❌ Erreur enrichissement théologique: ${err.message}\n\nVeuillez réessayer ou utiliser un autre mode de génération.`);
      setRubriquesStatus(p => ({ ...p, [rubriqueNumber]: "error" }));
    } finally {
      setIsLoading(false);
    }
  };

  // 2. Fonction ChatGPT (copiée de App.js)
  const handleChatGPTAction = () => {
    window.open('https://chatgpt.com/', '_blank');
  };

  // 3. Fonction Notes (copiée de App.js)  
  const handleNotesAction = () => {
    if (setCurrentPage) {
      setCurrentPage('notes');
    } else {
      console.warn("[RUBRIQUE] setCurrentPage not available");
    }
  };

  // 4. Fonction API Control Panel (basée sur ApiControlPanel)
  const handleApiAction = () => {
    console.log('[CONTROL BUTTON] API button clicked for Rubrique', rubriqueNumber);
    setIsApiModalOpen(true);
  };

  // 5. Nouvelle fonction "Lire la Bible" (copiée de App.js)
  const handleBibleAction = () => {
    if (selectedBook === "--") {
      alert("Veuillez d'abord sélectionner un livre de la Bible");
      return;
    }
    
    const bookCodes = {
      "Genèse":"GEN","Exode":"EXO","Lévitique":"LEV","Nombres":"NUM","Deutéronome":"DEU",
      "Josué":"JOS","Juges":"JDG","Ruth":"RUT","1 Samuel":"1SA","2 Samuel":"2SA",
      "1 Rois":"1KI","2 Rois":"2KI","1 Chroniques":"1CH","2 Chroniques":"2CH",
      "Esdras":"EZR","Néhémie":"NEH","Esther":"EST","Job":"JOB","Psaumes":"PSA",
      "Proverbes":"PRO","Ecclésiaste":"ECC","Cantique des cantiques":"SNG",
      "Ésaïe":"ISA","Jérémie":"JER","Lamentations":"LAM","Ézéchiel":"EZK","Daniel":"DAN",
      "Osée":"HOS","Joël":"JOL","Amos":"AMO","Abdias":"OBA","Jonas":"JON","Michée":"MIC",
      "Nahum":"NAM","Habacuc":"HAB","Sophonie":"ZEP","Aggée":"HAG","Zacharie":"ZEC","Malachie":"MAL",
      "Matthieu":"MAT","Marc":"MRK","Luc":"LUK","Jean":"JHN","Actes":"ACT",
      "Romains":"ROM","1 Corinthiens":"1CO","2 Corinthiens":"2CO","Galates":"GAL",
      "Éphésiens":"EPH","Philippiens":"PHP","Colossiens":"COL","1 Thessaloniciens":"1TH",
      "2 Thessaloniciens":"2TH","1 Timothée":"1TI","2 Timothée":"2TI","Tite":"TIT",
      "Philémon":"PHM","Hébreux":"HEB","Jacques":"JAS","1 Pierre":"1PE","2 Pierre":"2PE",
      "1 Jean":"1JN","2 Jean":"2JN","3 Jean":"3JN","Jude":"JUD","Apocalypse":"REV"
    };
    
    const code = bookCodes[selectedBook]; 
    if (!code) {
      alert("Livre non reconnu pour YouVersion");
      return;
    }
    
    let url = `https://www.bible.com/fr/bible/63/${code}`;
    if (selectedChapter !== "--") { 
      url += `.${selectedChapter}`; 
      if (selectedVerse !== "--") url += `.${selectedVerse}`; 
    }
    window.open(url, "_blank");
  };
  
  const getRubriqueColor = (number) => {
    // Couleurs variées pour les différentes rubriques
    const colors = {
      1: { start: '#8b5cf6', end: '#7c3aed', name: 'Violet' },
      2: { start: '#3b82f6', end: '#2563eb', name: 'Bleu' },
      3: { start: '#10b981', end: '#059669', name: 'Vert' },
      4: { start: '#f59e0b', end: '#d97706', name: 'Orange' },
      5: { start: '#ef4444', end: '#dc2626', name: 'Rouge' },
      6: { start: '#8b5cf6', end: '#7c3aed', name: 'Violet' },
      7: { start: '#06b6d4', end: '#0891b2', name: 'Cyan' },
      8: { start: '#84cc16', end: '#65a30d', name: 'Lime' },
      9: { start: '#f97316', end: '#ea580c', name: 'Orange' },
      10: { start: '#ec4899', end: '#db2777', name: 'Rose' },
      // Cycle pour les rubriques suivantes
      default: { start: '#6b7280', end: '#4b5563', name: 'Gris' }
    };
    
    return colors[number] || colors[((number - 1) % 10) + 1] || colors.default;
  };

  const rubriqueColor = getRubriqueColor(rubriqueNumber);

  const formatRubriqueContent = (content) => {
    if (!content) return '';
    
    // Formatage complet avec support des titres Markdown (comme formatContent de App.js)
    let formatted = content
      // Supprimer les s\u00E9parateurs horizontaux markdown (---, ***, ___)
      .replace(/^[-*_]{3,}$/gm, '')
      // Titres Markdown avec support Unicode whitespace
      .replace(/^#{1}[\s\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]+(.*?)$/gim, "<h1>$1</h1>")
      .replace(/^#{2}[\s\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]+(.*?)$/gim, "<h2>$1</h2>")
      .replace(/^#{3}[\s\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]+(.*?)$/gim, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*((?!\s)[^*]+)\*/g, '<em>$1</em>');
    
    // Splitter en paragraphes et wrapper sauf les titres
    formatted = formatted
      .split('\n\n')
      .map(p => {
        const trimmed = p.trim();
        if (!trimmed) return '';
        // Ne pas wrapper les titres
        if (trimmed.startsWith('<h1>') || trimmed.startsWith('<h2>') || trimmed.startsWith('<h3>')) {
          return trimmed;
        }
        return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
      })
      .join('');
    
    return formatted;
  };

  return (
    <div className="sub-page">
      {/* Header */}
      <div className="sub-header">
        <button className="sub-back" onClick={onGoBack}>← Retour</button>
        <div className="sub-header-center">
          <div className="sub-title">
            <span className="rub-color-dot" style={{ background: rubriqueColor.start }} />
            Rubrique {rubriqueNumber} — {rubriqueTitle}
          </div>
          {bookInfo && <div className="sub-sub">{bookInfo}</div>}
        </div>
        <div className="sub-actions">
          <button className="sub-btn" onClick={handleNotesAction} title="Prise de notes">Notes</button>
          <div className="sub-sep" />
          <button className="sub-btn" onClick={async () => {
            const result = await copyToClipboard(content);
            if (!result.success) alert(`Erreur copie: ${result.error}`);
          }} title="Copier">Copier</button>
          <button className="sub-btn" onClick={async () => {
            const result = await shareContent(rubriqueNumber, rubriqueTitle, content);
            if (!result.success && !result.error?.includes('non disponible')) alert(`Erreur partage: ${result.error}`);
          }} title="Partager">Partager</button>
          <button className="sub-btn" onClick={async () => {
            const result = await exportRubriqueToPDF(rubriqueNumber, rubriqueTitle, content);
            if (!result.success) alert(`Erreur PDF: ${result.error}`);
          }} title="Export PDF">PDF</button>
          <div className="sub-sep" />
          <button className="sub-btn" onClick={handleBibleAction} title="Lire la Bible">Bible</button>
        </div>
      </div>

      {/* Body */}
      <div className="sub-body">
        <div className="sub-content">
          {content && content.trim().length > 0 ? (
            <div className="sub-card">
              <div
                className="content-text"
                dangerouslySetInnerHTML={{ __html: formatRubriqueContent(content) }}
              />
            </div>
          ) : (
            <div className="sub-empty">
              <div className="empty-icon">⚡</div>
              <h3>Génération en cours...</h3>
              <p>Génération automatique du contenu théologique pour "{rubriqueTitle}"...</p>
            </div>
          )}

          {/* Navigation entre rubriques */}
          <div className="rub-nav">
            {rubriqueNumber > 1 && (
              <button className="sub-btn" onClick={() => onNavigateToRubrique(rubriqueNumber - 1)}>
                ◀ Rubrique {rubriqueNumber - 1}
              </button>
            )}
            {rubriqueNumber < 28 && (
              <button className="sub-btn primary" onClick={() => onNavigateToRubrique(rubriqueNumber + 1)}>
                Rubrique {rubriqueNumber + 1} ▶
              </button>
            )}
          </div>

          <div className="rub-footer">
            Rubrique {rubriqueNumber} sur 28 — Étude complète en 28 points
          </div>
        </div>
      </div>
    </div>
  );
};

export default RubriquePage;